// Gán bảng hoa hồng cho một CTV (§2.5.1 — D25). Trước khi xác nhận, xem trước trần hoa hồng:
// mức cao nhất của bảng phải ≤ chênh lệch nhỏ nhất của bạn trên CTV đó (giá bán cho CTV − giá
// nhập của bạn, ô thấp nhất). Vượt trần → chặn, vì bạn sẽ bị âm ở các đơn rơi vào ô đó.
// CTV chưa có bảng: áp dụng ngay tháng này; đổi bảng: từ tháng sau (D4).

import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, Info, Plus } from 'lucide-react'
import { get, post } from '@/lib/api'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { errorDetails, errorMessage } from '@/components/price-lists/types'
import { MODE_SHORT, monthLabel, shiftMonth, tiersSummary, versionAt, vnd, type CapCheck, type CtvRow, type Scheme } from './types'

export function AssignSchemeDialog({
  ctv,
  schemes,
  month,
  hasAssignment,
  onClose,
  onAssigned,
  initialSchemeId,
  onCreateNew,
}: {
  ctv: Pick<CtvRow, 'shopId' | 'name' | 'nextScheme'> & { scheme: { id: string; code: string; name: string } | null }
  /** Bảng chưa lưu trữ của tôi. */
  schemes: Scheme[]
  /** Tháng hiện tại (YYYY-MM). */
  month: string
  /** CTV đã từng được gán bảng (đổi bảng → từ tháng sau). */
  hasAssignment: boolean
  onClose: () => void
  onAssigned: (r: { effectiveFromMonth: string; code: string }) => void
  /** Chọn sẵn (vd. bảng vừa lập từ nút Tạo bảng chiết khấu) */
  initialSchemeId?: string
  /** Có thì hiện nút Tạo bảng chiết khấu (ngăn chi tiết cấp dưới — docs/KE_HOACH_BANG_GIA_KHI_TAO_DON_VI.md §4.6) */
  onCreateNew?: () => void
}) {
  const [schemeId, setSchemeId] = useState<string | null>(initialSchemeId ?? null)
  const [blocked, setBlocked] = useState<CapCheck | null>(null)
  const [error, setError] = useState<string | null>(null)
  const chosen = schemes.find((s) => s.id === schemeId) ?? null
  const effectiveFrom = hasAssignment ? shiftMonth(month, 1) : month
  // Bảng sẽ áp từ tháng sau (đã hẹn), hoặc đang áp nếu chưa hẹn đổi.
  const upcomingId = ctv.nextScheme?.id ?? ctv.scheme?.id ?? null

  const capQ = useQuery({
    queryKey: ['commissions', 'cap-preview', ctv.shopId, schemeId],
    queryFn: () => get<{ cap: CapCheck | null }>(`/commissions/cap-preview?ctvShopId=${encodeURIComponent(ctv.shopId)}&schemeId=${schemeId}`),
    enabled: !!schemeId,
    staleTime: 0,
  })
  const cap = capQ.data?.cap ?? null
  const capBlocked = (cap && !cap.ok) || (blocked && !blocked.ok)
  const shownCap = blocked ?? cap

  const assign = useMutation({
    mutationFn: () => post<{ effectiveFromMonth: string }>('/commissions/assign', { ctvShopId: ctv.shopId, schemeId }),
    onSuccess: (r) => onAssigned({ effectiveFromMonth: r.effectiveFromMonth, code: chosen?.code ?? '' }),
    onError: (e) => {
      const c = errorDetails<CapCheck>(e, 'commission.cap_exceeded')
      if (c && typeof c.maxRate === 'number') {
        setBlocked({ ...c, ok: false })
        setError(null)
        return
      }
      setError(errorMessage(e, 'Không gán được bảng chiết khấu'))
    },
  })

  return (
    <Dialog open onOpenChange={(o) => !o && !assign.isPending && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader className="pr-8">
          <DialogTitle>Gán bảng chiết khấu cho {ctv.name}</DialogTitle>
          <DialogDescription>
            {ctv.scheme ? `Đang áp: ${ctv.scheme.name} (${ctv.scheme.code}).` : 'CTV chưa có bảng chiết khấu.'}
            {ctv.nextScheme ? ` Từ tháng ${monthLabel(ctv.nextScheme.effectiveFromMonth)}: ${ctv.nextScheme.name}.` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {schemes.length === 0 ? (
            <p className="rounded-lg bg-warn/10 px-3 py-2 text-sm text-ink">Chưa có bảng chiết khấu nào.</p>
          ) : (
            <fieldset className="space-y-2">
              <legend className="mb-1.5 text-sm text-inkSoft">Chọn bảng</legend>
              {schemes.map((s) => {
                const v = versionAt(s, month)
                const tiers = v?.tiers ?? s.tiers
                const isUpcoming = s.id === upcomingId
                return (
                  <label
                    key={s.id}
                    className={cn(
                      'motion-state flex cursor-pointer gap-3 rounded-lg border p-3 text-sm',
                      schemeId === s.id ? 'border-accent bg-accent/5' : 'border-rule hover:bg-paperSoft',
                      isUpcoming && 'cursor-default opacity-60 hover:bg-transparent',
                    )}
                  >
                    <input
                      type="radio"
                      name="assign-scheme"
                      className="mt-1 accent-accent"
                      checked={schemeId === s.id}
                      disabled={isUpcoming}
                      onChange={() => {
                        setSchemeId(s.id)
                        setBlocked(null)
                        setError(null)
                      }}
                    />
                    <span className="min-w-0">
                      <span className="block font-medium text-ink">
                        {s.name} <span className="font-mono text-xs font-normal text-muted">{s.code}</span>
                        {isUpcoming && <span className="ml-1.5 text-xs font-normal text-muted">{ctv.nextScheme ? '· đã hẹn từ tháng sau' : '· đang áp'}</span>}
                      </span>
                      <span className="mt-0.5 block text-xs text-inkSoft">
                        {MODE_SHORT[v?.mode ?? s.mode]} · {tiersSummary(tiers)}
                      </span>
                    </span>
                  </label>
                )
              })}
            </fieldset>
          )}
          {onCreateNew && (
            <Button type="button" variant="outline" className="gap-1.5" onClick={onCreateNew} disabled={assign.isPending}>
              <Plus className="size-4" aria-hidden />
              Tạo bảng chiết khấu
            </Button>
          )}

          {schemeId && (
            <div aria-live="polite">
              {capQ.isLoading && !blocked ? (
                <p className="rounded-lg bg-paperSoft px-3 py-2 text-sm text-inkSoft">Đang kiểm tra…</p>
              ) : capQ.isError && !blocked ? (
                <p className="rounded-lg bg-warn/10 px-3 py-2 text-sm text-ink">Chưa kiểm tra được trần chiết khấu ({errorMessage(capQ.error)}).</p>
              ) : shownCap && !shownCap.ok ? (
                <div className="flex gap-2 rounded-lg bg-danger/5 p-3 text-sm text-danger">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                  <div className="space-y-1">
                    <p className="font-medium">
                      Không gán được: mức cao nhất {vnd(shownCap.maxRate)}/đơn vượt chênh lệch nhỏ nhất của bạn trên CTV này — {vnd(shownCap.minMargin)}
                      {shownCap.at ? ` (ô ${shownCap.at})` : ''}.
                    </p>
                  </div>
                </div>
              ) : shownCap ? (
                <p className="flex gap-2 rounded-lg bg-ok/10 p-3 text-sm text-ink">
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-ok" />
                  <span>
                    Hợp lệ: mỗi đơn bạn còn ít nhất {vnd(shownCap.minMargin - shownCap.maxRate)}.
                  </span>
                </p>
              ) : capQ.isSuccess ? (
                <p className="flex gap-2 rounded-lg bg-paperSoft p-3 text-sm text-inkSoft">
                  <Info className="mt-0.5 size-4 shrink-0" />
                  Chưa tính được trần: CTV chưa được gán bảng giá hoặc bạn chưa có giá nhập.
                </p>
              ) : null}
            </div>
          )}

          {schemeId && !capBlocked && (
            <p className="text-sm text-inkSoft">
              {hasAssignment
                ? `Áp dụng từ tháng ${monthLabel(effectiveFrom)}.`
                : `CTV chưa có bảng: áp dụng ngay từ tháng ${monthLabel(month)}.`}
            </p>
          )}

          {error && (
            <p className="flex items-start gap-2 rounded-lg bg-danger/5 px-3 py-2 text-sm text-danger">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:space-x-0">
          <Button variant="outline" onClick={onClose} disabled={assign.isPending}>
            Hủy
          </Button>
          <Button onClick={() => assign.mutate()} disabled={!schemeId || assign.isPending || capQ.isLoading || !!capBlocked}>
            {assign.isPending ? 'Đang gán…' : 'Gán bảng'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
